import { useState } from "react";

import { useTranslation } from "react-i18next";

import { AxiosError } from "axios";

import useAxiosPrivate from "../../../../../../api/hooks/useAxiosPrivate";
import { ALERT_STATUS } from "../../../../../../context/alert/types";
import {
  DRIVERS_ACTIONS,
  GeneralDialogProps,
  ROUTES,
  useMediaQueries
} from "../../../../../../shared";
import DialogActions from "../../../../../../shared/components/buttons/DialogActions";
import {
  cardHolderNameIsValid,
  descriptionIsValid,
  isDriverPayloadReadyToConfirm
} from "../../../../../../shared/functions/functions";
import useDriverIdInputValidation from "../../../../../../shared/hooks/useDriverIdInputValidation";
import useDriversFormInputs from "../../../../../../shared/hooks/useDriversFormInputs";
import useResponse from "../../../../../../shared/hooks/useResponse";
import useTableItemDetails from "../../../../../DetailsSection/hooks/useTableItemDetails";
import Dialog from "../../../../../Dialog/Dialog";
import { DialogWidth } from "../../../../../Dialog/types/enums";

import AddDriverDialogContent from "./AddDriverDialogContent";

const AddDriverDialog = ({ isOpen, onClose }: GeneralDialogProps) => {
  const { t } = useTranslation();
  const { toMd } = useMediaQueries();
  const { postData } = useAxiosPrivate();
  const { getTableItemDetails } = useTableItemDetails();
  const { handleResponse } = useResponse();

  const [cardHolderName, setCardHolderName] = useState<string | null>(null);
  const [cardHolderInputError, setCardHolderInputError] = useState<
    string | null
  >(null);
  const [driverId, setDriverId] = useState<string | null>(null);
  const [descriptionValue, setDescriptionValue] = useState<string | null>("");
  const [descriptionInputError, setDescriptionInputError] = useState<
    string | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);

  const { inputError, inputIsValid, clearInputError, validateInput } =
    useDriverIdInputValidation(driverId);

  const {
    validateCardHolderNameLength,
    validateDescriptionLength,
    handleCardHolderNameChange,
    handleChangeDriverIdValue,
    handleChangeDescription
  } = useDriversFormInputs({
    setCardHolderInputError,
    setDescriptionInputError,
    setCardHolderName,
    setDriverId,
    validateInput,
    clearInputError,
    setDescriptionValue
  });

  const payload = {
    cardNumber: driverId,
    cardName: cardHolderName?.trim(),
    comment: descriptionValue
  };

  const resetAllValues = () => {
    setCardHolderName(null);
    setCardHolderInputError(null);
    setDriverId(null);
    clearInputError();
    setDescriptionValue("");
    setDescriptionInputError(null);
  };

  const handleCloseAddDriverDialog = () => {
    onClose(DRIVERS_ACTIONS.AddDriver);
  };

  const handleSubmit = () => {
    const addDriver = async () => {
      try {
        setIsLoading(true);
        const response = await postData(ROUTES.Drivers, payload);
        const idFromResponseLocation = response?.headers.location;

        handleCloseAddDriverDialog();
        getTableItemDetails({
          itemId: idFromResponseLocation,
          successMessage: t("Alert##add driver success"),
          leaveTableDetailsNotUpdated: true
        });
      } catch (error) {
        const err = error as AxiosError<any>;
        if (err) {
          const responseData: string = err.response?.data
            ? err.response?.data.message
            : t("Alert##action failed");
          handleResponse(ALERT_STATUS.Critical, responseData);
        }
      } finally {
        setIsLoading(false);
      }
    };

    addDriver();
  };

  return (
    <Dialog
      data-testid="add-driver-dialog"
      title={t("Dialog##add driver")}
      extendToMaxWidth={toMd}
      isTitleSeparator
      isActionsSeparator
      open={isOpen}
      close={handleCloseAddDriverDialog}
      submit={handleSubmit}
      width={toMd ? undefined : DialogWidth.MinWidth}
      slotProps={{ transition: { onExited: () => resetAllValues() } }}
      actions={
        <DialogActions
          onClose={handleCloseAddDriverDialog}
          isLoading={isLoading}
          isreadyToConfirm={isDriverPayloadReadyToConfirm(
            cardHolderName,
            cardHolderNameIsValid(cardHolderName),
            driverId,
            inputIsValid,
            descriptionIsValid(descriptionValue)
          )}
          confirmationButtonText={t("Button##add")}
          confirmationButtonTestId="add-driver-button"
          cancelButtonText={t("Button##close")}
          cancelButtonTestId="close-add-driver"
        />
      }
    >
      <AddDriverDialogContent
        onCardHolderNameChange={handleCardHolderNameChange}
        validateCardHolderName={validateCardHolderNameLength}
        cardHolderNameErrorText={
          cardHolderNameIsValid(cardHolderName) ? "" : cardHolderInputError
        }
        validateIdInput={validateInput}
        idInputError={inputError}
        onIdValueChange={handleChangeDriverIdValue}
        onDescriptionChange={handleChangeDescription}
        validateDescription={validateDescriptionLength}
        descriptionErrorText={
          descriptionIsValid(descriptionValue) ? "" : descriptionInputError
        }
        isLoading={isLoading}
      />
    </Dialog>
  );
};

export default AddDriverDialog;
